
// @ts-ignore
import instance from "../api/instance";
import collectSystemInfo from "./systemInfoCollector";
import { formatErrorReport, generateErrorReportHTML, generateErrorReportText } from "./errorReportFormatter";

interface EnhancedErrorLog {
  message: string;
  route?: string;
  errorDetails: {
    name: string;
    message: string;
    stack: string;
    cause?: any;
    componentStack?: string;
    memoryInfo?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
      usagePercentage: number;
    };
    originalError?: any;
  };
  userInfo: {
    userAgent: string;
    browser: {
      name: string;
      version: string;
      engine: string;
    };
    os: {
      name: string;
      version: string;
      platform: string;
    };
    device: {
      type: string;
      vendor: string;
      model: string;
    };
    screen: {
      width: number;
      height: number;
      colorDepth: number;
      pixelRatio: number;
    };
    viewport: {
      width: number;
      height: number;
    };
    language: string;
    timezone: string;
    cookiesEnabled: boolean;
    localStorage: boolean;
    sessionStorage: boolean;
    online: boolean;
    connection?: {
      effectiveType: string;
      downlink: number;
      rtt: number;
    };
  };
  appInfo: {
    url: string;
    referrer: string;
    timestamp: string;
    sessionId: string;
    userId?: string;
    userRole?: string;
  };
  performance: {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    timing: {
      navigationStart: number;
      loadEventEnd: number;
      domContentLoadedEventEnd: number;
    };
  };
  context: {
    reduxState?: any;
    localStorage?: any;
    sessionStorage?: any;
    websocketContext?: string;
    isWebSocketError?: boolean;
    interviewState?: string;
  };
}

// Type declarations for window object extensions
declare global {
  interface Window {
    __REDUX_STORE__?: any;
    __REDUX_DEVTOOLS_EXTENSION__?: any;
    __REDUX_STATE__?: any;
    __ZUSTAND_STORES__?: any;
  }
}

// Browser detection utility
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = { name: 'Unknown', version: 'Unknown', engine: 'Unknown' };
  
  if (userAgent.includes('Chrome')) {
    browser = { name: 'Chrome', version: userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown', engine: 'Blink' };
  } else if (userAgent.includes('Firefox')) {
    browser = { name: 'Firefox', version: userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown', engine: 'Gecko' };
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = { name: 'Safari', version: userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown', engine: 'WebKit' };
  } else if (userAgent.includes('Edge')) {
    browser = { name: 'Edge', version: userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown', engine: 'EdgeHTML' };
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    browser = { name: 'Internet Explorer', version: userAgent.match(/(?:MSIE|rv:)\s*(\d+)/)?.[1] || 'Unknown', engine: 'Trident' };
  }
  
  return browser;
};

// OS detection utility
const getOSInfo = () => {
  const userAgent = navigator.userAgent;
  let os: { name: string; version: string; platform: string } = { name: 'Unknown', version: 'Unknown', platform: navigator.platform };
  
  if (userAgent.includes('Windows')) {
    os = { name: 'Windows', version: userAgent.match(/Windows NT (\d+\.\d+)/)?.[1] || 'Unknown', platform: 'Win32' };
  } else if (userAgent.includes('Mac')) {
    os = { name: 'macOS', version: userAgent.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown', platform: 'MacIntel' };
  } else if (userAgent.includes('Linux')) {
    os = { name: 'Linux', version: 'Unknown', platform: 'Linux x86_64' };
  } else if (userAgent.includes('Android')) {
    os = { name: 'Android', version: userAgent.match(/Android (\d+\.\d+)/)?.[1] || 'Unknown', platform: 'Android' };
  } else if (userAgent.includes('iOS')) {
    os = { name: 'iOS', version: userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown', platform: 'iPhone' };
  }
  
  return os;
};

// Device detection utility
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let device = { type: 'desktop', vendor: 'Unknown', model: 'Unknown' };
  
  if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    device.type = 'mobile';
    if (userAgent.includes('iPhone')) {
      device = { type: 'mobile', vendor: 'Apple', model: 'iPhone' };
    } else if (userAgent.includes('iPad')) {
      device = { type: 'tablet', vendor: 'Apple', model: 'iPad' };
    } else if (userAgent.includes('Android')) {
      device = { type: 'mobile', vendor: 'Android', model: 'Android Device' };
    }
  }
  
  return device;
};

// Network information
const getNetworkInfo = () => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  return {
    effectiveType: connection?.effectiveType || 'unknown',
    downlink: connection?.downlink || 0,
    rtt: connection?.rtt || 0
  };
};

// Performance information
const getPerformanceInfo = () => {
  const performance = window.performance;
  const memory = (performance as any).memory;
  
  return {
    memory: memory ? {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit
    } : undefined,
    timing: {
      navigationStart: performance.timing?.navigationStart || 0,
      loadEventEnd: performance.timing?.loadEventEnd || 0,
      domContentLoadedEventEnd: performance.timing?.domContentLoadedEventEnd || 0
    }
  };
};

// Generate session ID
const generateSessionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get session ID (create if doesn't exist)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('errorSessionId');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('errorSessionId', sessionId);
  }
  return sessionId;
};

// Get user context from Redux store
const getUserContext = () => {
  try {
    let userId, userRole;
    
    // Method 1: Try to get from Redux store directly (most reliable)
    try {
      const store = window.__REDUX_STORE__;
      if (store && store.getState) {
        const state = store.getState();
        if (state?.auth?.user) {
          userId = state.auth.user.user_id || state.auth.user.id;
          userRole = state.auth.user.role;
        }
      }
    } catch (e) {
      // Silent fail
    }
    
    // Method 2: Try to get from Redux DevTools
    if (!userId || !userRole) {
      try {
        const reduxState = window.__REDUX_DEVTOOLS_EXTENSION__?.connect()?.getState?.();
        if (reduxState?.auth?.user) {
          userId = userId || reduxState.auth.user.user_id || reduxState.auth.user.id;
          userRole = userRole || reduxState.auth.user.role;
        }
      } catch (e) {
        // Silent fail
      }
    }
    
    // Method 3: Try to get from window.__REDUX_STATE__ if available
    if (!userId || !userRole) {
      try {
        const reduxState = window.__REDUX_STATE__;
        if (reduxState?.auth?.user) {
          userId = userId || reduxState.auth.user.user_id || reduxState.auth.user.id;
          userRole = userRole || reduxState.auth.user.role;
        }
      } catch (e) {
        // Silent fail
      }
    }
    
    // Method 4: Try localStorage with different keys
    if (!userId || !userRole) {
      try {
        const possibleKeys = ['user', 'authUser', 'userData', 'currentUser'];
        for (const key of possibleKeys) {
          const userData = localStorage.getItem(key);
          if (userData) {
            const user = JSON.parse(userData);
            userId = userId || user.user_id || user.id;
            userRole = userRole || user.role;
            if (userId || userRole) {
              break;
            }
          }
        }
      } catch (e) {
        // Silent fail
      }
    }
    
    // Method 5: Try sessionStorage
    if (!userId || !userRole) {
      try {
        const possibleKeys = ['user', 'authUser', 'userData', 'currentUser'];
        for (const key of possibleKeys) {
          const userData = sessionStorage.getItem(key);
          if (userData) {
            const user = JSON.parse(userData);
            userId = userId || user.user_id || user.id;
            userRole = userRole || user.role;
            if (userId || userRole) {
              break;
            }
          }
        }
      } catch (e) {
        // Silent fail
      }
    }
    
    // Method 6: Try to get from Zustand stores if available
    if (!userId || !userRole) {
      try {
        const zustandStores = window.__ZUSTAND_STORES__;
        if (zustandStores?.userStore?.getState) {
          const userState = zustandStores.userStore.getState();
          userId = userId || userState.user?.user_id || userState.user?.id;
          userRole = userRole || userState.user?.role;
        }
      } catch (e) {
        // Silent fail
      }
    }
    
    const result = {
      userId: userId || undefined,
      userRole: userRole ? String(userRole) : undefined,
      email: undefined,
      name: undefined
    };
    
    return result;
  } catch (error) {
    return {
      userId: undefined,
      userRole: undefined,
      email: undefined,
      name: undefined
    };
  }
};

// Get context data
const getContextData = () => {
  try {
    const localStorageData: Record<string, string> = {};
    const sessionStorageData: Record<string, string> = {};
    
    // Sample localStorage data (be careful with sensitive info)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !key.includes('password') && !key.includes('token') && !key.includes('secret')) {
        try {
          const value = localStorage.getItem(key);
          if (value !== null) {
            localStorageData[key] = value;
          }
        } catch (e) {
          localStorageData[key] = '[Error reading]';
        }
      }
    }
    
    // Sample sessionStorage data
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && !key.includes('password') && !key.includes('token') && !key.includes('secret')) {
        try {
          const value = sessionStorage.getItem(key);
          if (value !== null) {
            sessionStorageData[key] = value;
          }
        } catch (e) {
          sessionStorageData[key] = '[Error reading]';
        }
      }
    }
    
    return {
      localStorage: localStorageData,
      sessionStorage: sessionStorageData
    };
  } catch (error) {
    return {
      localStorage: { error: 'Could not read localStorage' },
      sessionStorage: { error: 'Could not read sessionStorage' }
    };
  }
};

// Track recent errors to prevent duplicate emails
const recentErrors = new Map<string, number>();
const ERROR_DEBOUNCE_TIME = 5000; // 5 seconds
let isProcessingError = false; // Global flag to prevent concurrent error processing
let errorLoggingDepth = 0; // Track recursion depth
const MAX_ERROR_LOGGING_DEPTH = 2; // Maximum recursion depth
let consecutiveFailures = 0; // Track consecutive failures
const MAX_CONSECUTIVE_FAILURES = 3; // Disable logging after 3 consecutive failures
let isLoggingDisabled = false; // Flag to temporarily disable logging

// Check if error is recent to prevent duplicate emails
const isRecentError = (error: Error, route?: string): boolean => {
  const errorKey = `${error.message}-${route}-${error.stack?.split('\n')[1] || ''}`;
  const now = Date.now();
  const lastErrorTime = recentErrors.get(errorKey);
  
  if (lastErrorTime && (now - lastErrorTime) < ERROR_DEBOUNCE_TIME) {
    return true; // Error is recent, don't send duplicate
  }
  
  recentErrors.set(errorKey, now);
  return false;
};

// Check if error is from the logging system itself
const isLoggingSystemError = (error: Error): boolean => {
  const errorMessage = error.message.toLowerCase();
  const errorStack = error.stack?.toLowerCase() || '';
  
  return errorMessage.includes('enhanced error report') ||
         errorMessage.includes('failed to send enhanced error log') ||
         errorMessage.includes('failed to send fallback error log') ||
         errorStack.includes('logenhancederror') ||
         errorStack.includes('enhancederrorlogger') ||
         errorMessage.includes('network error') ||
         errorMessage.includes('fetch error');
};

// Ignore known non-actionable runtime errors
const isIgnorableRuntimeError = (error: Error): boolean => {
  const name = (error?.name || '').toLowerCase();
  const message = (error?.message || '').toLowerCase();
  // Webpack chunk load failures are common on deploys or flaky networks
  return (
    name === 'chunkloaderror' ||
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    /loading\s+chunk\s+\d+\s+failed/.test(message)
  );
};

// Clean up old error entries
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of recentErrors.entries()) {
    if (now - timestamp > ERROR_DEBOUNCE_TIME) {
      recentErrors.delete(key);
    }
  }
}, 60000); // Clean up every minute

// Helper function to safely stringify objects with circular references
const safeStringify = (obj: any, maxDepth: number = 3): string => {
  const seen = new WeakSet();
  
  const stringify = (value: any, depth: number): string => {
    // Handle primitives
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value !== 'object') return String(value);
    
    // Prevent circular references
    if (seen.has(value)) return '[Circular Reference]';
    seen.add(value);
    
    // Prevent deep nesting
    if (depth >= maxDepth) return '[Max Depth Reached]';
    
    // Handle DOM elements
    if (value instanceof HTMLElement) {
      return `[HTMLElement: ${value.tagName}]`;
    }
    
    // Handle other special objects
    if (value instanceof Error) {
      return `Error: ${value.message}`;
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      return `[${value.map(item => stringify(item, depth + 1)).join(', ')}]`;
    }
    
    // Handle plain objects
    try {
      const props = Object.keys(value)
        .filter(key => !key.startsWith('__react') && !key.startsWith('_')) // Filter React internals
        .slice(0, 10) // Limit properties
        .map(key => {
          try {
            return `${key}: ${stringify(value[key], depth + 1)}`;
          } catch (e) {
            return `${key}: [Error]`;
          }
        });
      return `{${props.join(', ')}}`;
    } catch (e) {
      return '[Object]';
    }
  };
  
  try {
    return stringify(obj, 0);
  } catch (e) {
    return '[Unable to stringify]';
  }
};

const parseErrorObject = (error: any): Error => {
  if (error instanceof Error) {
    return error;
  }

  // Handle plain objects
  if (typeof error === 'object' && error !== null) {
    const message = error.message || safeStringify(error);
    const newError = new Error(message);
    
    // Safely copy properties, excluding circular references and DOM elements
    try {
      Object.keys(error).forEach(key => {
        if (!key.startsWith('__react') && 
            !key.startsWith('_') && 
            !(error[key] instanceof HTMLElement)) {
          try {
            (newError as any)[key] = error[key];
          } catch (e) {
            // Skip properties that can't be copied
          }
        }
      });
    } catch (e) {
      // Silent fail on property copying
    }
    
    return newError;
  }

  // Handle primitives
  return new Error(String(error));
};

const logEnhancedError = async (
  errorInput: any,
  errorInfo?: React.ErrorInfo,
  route?: string,
  context?: string
): Promise<void> => {
  // ============================================================
  // SIMPLIFIED ERROR LOGGING: ONLY WebSocket disconnections in actual AI interviews
  // 
  // ✅ SENDS EMAIL FOR:
  //    - WebSocket errors where context === 'actual-interview'
  //    - Called from: useInterviewWebSocket.js (lines 194, 222)
  // 
  // ❌ IGNORES (NO EMAIL):
  //    - All errors from App.js (global error handlers) - no context param
  //    - All errors from ErrorBoundary.js (ResumeBuilder) - no context param  
  //    - All errors from EnhancedErrorBoundary.jsx - no context param
  //    - All non-WebSocket errors
  //    - All practice/demo interviews
  // ============================================================
  
  // Parse error object
  const error = parseErrorObject(errorInput);
  
  // Check if this is a WebSocket error
  const isWebSocketError = error.message.toLowerCase().includes('websocket') || 
                          error.message.toLowerCase().includes('socket');
  
  // FIRST FILTER: ONLY log WebSocket errors - ignore all other errors
  // This blocks: App.js errors, ErrorBoundary errors, React errors, etc.
  if (!isWebSocketError) {
    return;
  }
  
  // SECOND FILTER: ONLY log errors from actual interviews
  // This blocks: practice interviews, demo interviews, calls without context param
  if (context !== 'actual-interview') {
    return;
  }
  
  // If we reach here: It's a WebSocket error from an actual interview ✅
  
  // Check if logging is temporarily disabled
  if (isLoggingDisabled) {
    return;
  }
  
  // Prevent concurrent error processing
  if (isProcessingError) {
    return;
  }
  
  // Prevent duplicate emails - use 10 minute debounce to ensure only ONE email per disconnection
  const websocketErrorKey = `websocket-${route}-${context}`;
  const now = Date.now();
  const lastErrorTime = recentErrors.get(websocketErrorKey);
  const WEBSOCKET_DEBOUNCE_TIME = 600000; // 10 minutes - ensures only ONE email per actual disconnection
  
  if (lastErrorTime && (now - lastErrorTime) < WEBSOCKET_DEBOUNCE_TIME) {
    return;
  }
  
  // Log this error timestamp to prevent duplicates
  recentErrors.set(websocketErrorKey, now);
  
  // Add memory info if available
  const memoryInfo = window.performance?.memory ? {
    usedJSHeapSize: window.performance.memory.usedJSHeapSize,
    totalJSHeapSize: window.performance.memory.totalJSHeapSize,
    jsHeapSizeLimit: window.performance.memory.jsHeapSizeLimit
  } : undefined;
  
  try {
    isProcessingError = true;

    // Collect comprehensive system information
    const systemInfo = await collectSystemInfo();
    
    // Get user context - prioritize context from error object, then fallback to global methods
    let userContext = getUserContext();
    if ((error as any).userContext) {
      userContext = {
        userId: (error as any).userContext.userId || userContext.userId,
        userRole: (error as any).userContext.userRole || userContext.userRole,
        email: (error as any).userContext.email,
        name: (error as any).userContext.name
      };
    }
    
    const contextData = getContextData();
    const sessionId = getSessionId();

    // Format error report for non-technical users with userRole
    const formattedReport = formatErrorReport(error, systemInfo, route, userContext.userRole);
    
    // Generate both HTML and text versions
    const htmlReport = generateErrorReportHTML(formattedReport);
    const textReport = generateErrorReportText(formattedReport);
    
    // Create the enhanced error log with both formatted and raw data
    const enhancedErrorLog: EnhancedErrorLog = {
      message: error.message.includes('Enhanced Error Report') ? error.message : `Enhanced Error Report - ${error.message}`,
      route,
      errorDetails: {
        name: error.name,
        message: error.message,
        stack: error.stack || '',
        cause: (error as any).cause,
        memoryInfo: memoryInfo ? {
          ...memoryInfo,
          usagePercentage: (memoryInfo.usedJSHeapSize / memoryInfo.totalJSHeapSize) * 100
        } : undefined,
        originalError: error !== errorInput ? errorInput : undefined
      },
      userInfo: {
        userAgent: systemInfo.browser.userAgent,
        browser: {
          name: systemInfo.browser.name,
          version: systemInfo.browser.version,
          engine: systemInfo.browser.engine
        },
        os: {
          name: systemInfo.os.name,
          version: systemInfo.os.version,
          platform: systemInfo.os.platform
        },
        device: {
          type: systemInfo.device.type,
          vendor: systemInfo.device.vendor,
          model: systemInfo.device.model
        },
        screen: {
          width: systemInfo.device.screen.width,
          height: systemInfo.device.screen.height,
          colorDepth: systemInfo.device.screen.colorDepth,
          pixelRatio: systemInfo.device.screen.pixelRatio
        },
        viewport: {
          width: systemInfo.device.viewport.width,
          height: systemInfo.device.viewport.height
        },
        language: systemInfo.browser.language,
        timezone: systemInfo.app.timezone,
        cookiesEnabled: systemInfo.browser.cookieEnabled,
        localStorage: systemInfo.features.localStorage,
        sessionStorage: systemInfo.features.sessionStorage,
        online: systemInfo.browser.onLine,
        connection: systemInfo.network.connection
      },
      appInfo: {
        url: systemInfo.app.url,
        referrer: systemInfo.app.referrer,
        timestamp: systemInfo.app.timestamp,
        sessionId,
        userId: userContext.userId,
        userRole: userContext.userRole
      },
      performance: systemInfo.performance,
      context: contextData
    };

    // Add React error info if available
    if (errorInfo) {
      enhancedErrorLog.errorDetails = {
        ...enhancedErrorLog.errorDetails,
        componentStack: errorInfo.componentStack || undefined
      };
    }

    // Add websocket-specific context (we already know it's a WebSocket error from actual interview)
    enhancedErrorLog.context = {
      ...enhancedErrorLog.context,
      websocketContext: context,
      isWebSocketError: true,
      // Add interview state information if available in error message
      interviewState: error.message.includes('State:') ? 
        error.message.match(/State: (\w+)/)?.[1] || 'unknown' : undefined
    };

    // Send both formatted and raw data with timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Error logging timeout')), 10000); // 10 second timeout
    });
    
    await Promise.race([
      instance.post<EnhancedErrorLog>('/core/send-mail-log/', { 
        message: textReport,
        htmlMessage: htmlReport,
        enhancedErrorData: enhancedErrorLog,
        formattedReport: formattedReport
      }),
      timeoutPromise
    ]);
    
    consecutiveFailures = 0; // Reset failure counter on success
  } catch (logError: unknown) {
    consecutiveFailures++;
    
    // Disable logging if too many consecutive failures
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      isLoggingDisabled = true;
      console.warn('[Error Logger] Too many failures, disabling logging for 5 minutes');
      
      // Re-enable after 5 minutes
      setTimeout(() => {
        isLoggingDisabled = false;
        consecutiveFailures = 0;
      }, 300000); // 5 minutes
    }
    
    // Simplified fallback - no retry for websocket errors to avoid spam
    // Just log to console
    console.error('[Error Logger] WebSocket error details:', {
      message: error.message,
      route,
      context,
      stack: error.stack
    });
  } finally {
    isProcessingError = false;
  }
};

export default logEnhancedError; 